import React, { useState, useEffect } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone, Mail, MapPin, ChevronRight, ChevronDown } from 'lucide-react';
import { SiLinkedin, SiFacebook, SiInstagram } from 'react-icons/si';
import { ROUTE_PATHS, cn } from '@/lib/index';
interface LayoutProps {
  children: React.ReactNode;
}
export function Layout({
  children
}: LayoutProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const location = useLocation();
  

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    setIsMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location]);
  const navLinks = [{
    name: 'Start',
    path: ROUTE_PATHS.HOME
  }, {
    name: 'O mnie',
    path: ROUTE_PATHS.ABOUT
  }, {
    name: 'Usługi',
    path: ROUTE_PATHS.SERVICES,
    hasDropdown: true,
    dropdownItems: [
      { name: 'Indywidualne', path: ROUTE_PATHS.SERVICES_INDIVIDUAL },
      { name: 'Dla firm', path: ROUTE_PATHS.SERVICES_BUSINESS }
    ]
  }, {
    name: 'Opinie',
    path: ROUTE_PATHS.REVIEWS
  }, {
    name: 'Galeria',
    path: ROUTE_PATHS.GALLERY
  }, {
    name: 'Kontakt',
    path: ROUTE_PATHS.CONTACT
  }];
  return <div className="flex flex-col min-h-screen bg-background text-foreground selection:bg-primary/10 selection:text-primary">
      {/* Navigation Header */}
      <header className={cn("fixed top-0 left-0 right-0 z-50 h-20 transition-all duration-300 border-b", isScrolled ? "bg-background/80 backdrop-blur-md border-border shadow-sm" : "bg-transparent border-transparent")}>
        <div className="container mx-auto px-6 h-full flex items-center justify-between">
          <Link to={ROUTE_PATHS.HOME} className="group flex items-center gap-3">
            <div className="flex flex-col leading-none">
              <span className="font-serif text-2xl md:text-3xl font-semibold tracking-tight text-primary">
                Rachela Nowak
              </span>
              <span className="text-[10px] uppercase tracking-[0.2em] font-medium text-muted-foreground group-hover:text-accent transition-colors">
                Psycholog • Seksuolog • Mediator
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map(link => <NavLink key={link.path} to={link.path} className={({
            isActive
          }) => cn("text-sm font-medium tracking-wide transition-colors hover:text-primary relative py-2", isActive ? "text-primary" : "text-muted-foreground")}>
                {({
              isActive
            }) => <>
                    {link.name}
                    {isActive && <motion.div layoutId="nav-underline" className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent" transition={{
                type: "spring",
                stiffness: 380,
                damping: 30
              }} />}
                  </>}
              </NavLink>)}
            <a href="https://www.znanylekarz.pl/rachela-nowak/psycholog/kalisz" target="_blank" rel="noopener noreferrer" className="bg-primary text-primary-foreground px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-primary/90 transition-all shadow-md active:scale-95">
              Umów wizytę
            </a>
          </nav>

          {/* Mobile Toggle */}
          <button className="md:hidden p-2 text-foreground hover:bg-muted rounded-lg transition-colors" onClick={() => setIsMenuOpen(!isMenuOpen)} aria-label="Toggle menu">
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMenuOpen && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} className="fixed inset-0 z-40 md:hidden bg-background pt-24 px-6">
            <nav className="flex flex-col gap-6">
              {navLinks.map(link => <NavLink key={link.path} to={link.path} className={({
            isActive
          }) => cn("text-2xl font-serif font-medium transition-colors", isActive ? "text-primary" : "text-muted-foreground")}>
                  {link.name}
                </NavLink>)}
              <div className="pt-6 border-t border-border flex flex-col gap-4">
                <p className="text-sm font-medium text-muted-foreground uppercase tracking-widest">Kontakt</p>
                <a href="tel:+48000000000" className="flex items-center gap-3 text-lg font-medium">
                  <Phone size={18} className="text-accent" /> +48 000 000 000
                </a>
                <a href="mailto:kontakt@rachelanowak.pl" className="flex items-center gap-3 text-lg font-medium">
                  <Mail size={18} className="text-accent" /> kontakt@rachelanowak.pl
                </a>
              </div>
            </nav>
          </motion.div>}
      </AnimatePresence>

      {/* Main Content */}
      <main className="flex-grow pt-20">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-secondary border-t border-border mt-auto">
        <div className="container mx-auto px-6 py-16 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Brand Column */}
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="flex flex-col">
                  <span className="font-serif text-2xl font-semibold text-primary">Rachela Nowak</span>
                  <span className="text-xs uppercase tracking-widest text-muted-foreground">Ekspert Psychologii & Mediacji</span>
                </div>
              </div>
              <p className="text-muted-foreground leading-relaxed text-sm">
                Łączę światy psychologii, prawa i biznesu, wspierając w budowaniu relacji, rozwiązywaniu konfliktów i dbaniu o dobrostan psychiczny.
              </p>
              <div className="flex items-center gap-4">
                <a href="#" className="p-2 rounded-full bg-background border border-border text-muted-foreground hover:text-primary hover:border-primary transition-all">
                  <SiLinkedin size={18} />
                </a>
                <a href="https://www.facebook.com/rachelanowakpsychseks" target="_blank" rel="noopener noreferrer" className="p-2 rounded-full bg-background border border-border text-muted-foreground hover:text-primary hover:border-primary transition-all">
                  <SiFacebook size={18} />
                </a>
                <a href="https://www.instagram.com/spec_od_psychy/" target="_blank" rel="noopener noreferrer" className="p-2 rounded-full bg-background border border-border text-muted-foreground hover:text-primary hover:border-primary transition-all">
                  <SiInstagram size={18} />
                </a>
              </div>
            </div>



            {/* Areas Column */}
            <div>
              <h4 className="font-semibold text-foreground mb-6">Obszary pracy</h4>
              <ul className="space-y-4 text-sm text-muted-foreground">
                <li>Psychoterapia CBT</li>
                <li>Seksuologia kliniczna</li>
                <li>Mediacje sądowe i cywilne</li>
                <li>Doradztwo biznesowe & MBA</li>
                <li>Wsparcie kobiet w biznesie</li>
              </ul>
            </div>

            {/* Contact Column */}
            <div className="space-y-6">
              <h4 className="font-semibold text-foreground mb-6">Kontakt</h4>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <MapPin size={18} className="text-accent mt-0.5 shrink-0" />
                  <p className="text-sm text-muted-foreground">Ostrów Wielkopolski, Kalisz, Warszawa</p>
                </div>
                <div className="flex items-center gap-3">
                  <Phone size={18} className="text-accent shrink-0" />
                  <a href="tel:+48000000000" className="text-sm text-muted-foreground hover:text-primary transition-colors">+48 786-967-470</a>
                </div>
                <div className="flex items-center gap-3">
                  <Mail size={18} className="text-accent shrink-0" />
                  <a href="mailto:kontakt@rachelanowak.pl" className="text-sm text-muted-foreground hover:text-primary transition-colors">gabinetrachelanowak@gmail.com</a>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-16 pt-8 border-t border-border flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs text-muted-foreground">
              &copy; 2026 Rachela Nowak. Wszystkie prawa zastrzeżone.
            </p>
            <div className="flex gap-6">
              <Link to="/polityka-prywatnosci" className="text-xs text-muted-foreground hover:text-primary transition-colors">Polityka prywatności</Link>
              <Link to="/regulamin" className="text-xs text-muted-foreground hover:text-primary transition-colors">Regulamin</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>;
}